import { getAddress, isAddress } from "@ethersproject/address";
import { identity, pipe } from "fp-ts/lib/function";
import * as t from "io-ts";
import { either } from "fp-ts";
import { Eq } from "fp-ts/lib/Eq";

interface ChecksumAddressBrand {
  readonly ChecksumAddress: unique symbol;
}

/**
 * @deprecated use `Address` instead
 */
export type ChecksumAddress = t.Branded<string, ChecksumAddressBrand>;

interface ChecksumAddressBrandC
  extends t.Type<ChecksumAddress, string, unknown> {}

/**
 * A codec that succeeds if an Ethereum address is checksum.
 *
 * WARNING: only checks if an address is a checksum - does not transform address
 * into checksum. Use the `ChecksumAddress` codec for that.
 */
export const ChecksumAddressBrandC: ChecksumAddressBrandC = t.brand(
  t.string,
  (s): s is ChecksumAddress => isAddress(s),
  "ChecksumAddress"
);

/**
 * Ethereum address that has been converted to a checksum address.
 *
 * Should always be used for addresses instead of `string`.
 */
export const ChecksumAddress = new t.Type<ChecksumAddress, string, unknown>(
  "ChecksumAddress",
  ChecksumAddressBrandC.is,
  (u, c) => {
    return pipe(
      /**
       * Could use ChecksumAddressBrandC.validate here, but that means we would
       * end up doing checking the checksum twice (isAddress in the brand codec
       * + getAddress below).
       */
      t.string.validate(u, c),
      either.chain((address) => {
        try {
          return t.success(getAddress(address) as ChecksumAddress);
        } catch (error) {
          return t.failure(u, c);
        }
      })
    );
  },
  (address) => address
);

export const isChecksumAddress = ChecksumAddressBrandC.is;

export const getChecksumAddressOrThrow = (address: string) =>
  pipe(
    address,
    ChecksumAddress.decode,
    either.fold(() => {
      throw new Error(`Invalid address: ${address}`);
    }, identity)
  );

export const getChecksumAddressOrNull = (address: string) =>
  pipe(
    address,
    ChecksumAddress.decode,
    either.fold(() => {
      return null;
    }, identity)
  );

export const checksumAddressEq: Eq<ChecksumAddress> = {
  equals: (a, b) => a === b,
};

export const CHECKSUM_ADDRESS_ZERO = getChecksumAddressOrThrow(
  "0x0000000000000000000000000000000000000000"
);
